import { Menu, MenuItem } from "@mui/material";
import CustomTooltip from "../../Assets/customTheme/customtooltip";
import { Fade } from "@mui/material";
import { useNotification } from "./NotificationProvider";
import { MyNotificationsNoneRoundedIcon } from "../../Assets/svg/MyIcons";
import {
  BellRing,
  CheckCheck,
  CheckCircle2,
  Clock3,
  FileCheck2,
  XCircle,
} from "lucide-react";
import { Link } from "react-router-dom";
import "./SocketNotification.css";

const getNotificationMeta = (message = "") => {
  const normalized = message.toLowerCase();

  if (
    normalized.includes("rejected") ||
    normalized.includes("failed") ||
    normalized.includes("error")
  ) {
    return { tone: "danger", icon: XCircle };
  }

  if (
    normalized.includes("approved") ||
    normalized.includes("success") ||
    normalized.includes("updated")
  ) {
    return { tone: "success", icon: CheckCircle2 };
  }

  if (
    normalized.includes("request") ||
    normalized.includes("submitted") ||
    normalized.includes("created")
  ) {
    return { tone: "primary", icon: FileCheck2 };
  }

  return { tone: "neutral", icon: BellRing };
};

const EmptyNotificationsArt = () => (
  <svg
    className="mp-notification-empty-art"
    viewBox="0 0 120 120"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <circle cx="60" cy="60" r="52" fill="#EEF2FF" />
    <circle cx="60" cy="60" r="40" fill="#FFFFFF" stroke="#D9E2F5" strokeWidth="2" />
    <path
      d="M43 52.5c0-9.4 7.6-17 17-17s17 7.6 17 17v10.2c0 1.5.4 3 1.1 4.3l2.4 4.3c1.4 2.5-.4 5.7-3.3 5.7H42.8c-2.9 0-4.7-3.2-3.3-5.7l2.4-4.3c.7-1.3 1.1-2.8 1.1-4.3V52.5Z"
      fill="#C7D4F0"
    />
    <path
      d="M52.5 79.5c1.5 3.3 4.7 5.5 8.5 5.5s7-2.2 8.5-5.5"
      stroke="#22419E"
      strokeWidth="3"
      strokeLinecap="round"
    />
    <circle cx="84" cy="38" r="12" fill="#22419E" />
    <path
      d="M79.5 38.2 82.7 41.3 88.8 34.8"
      stroke="#FFFFFF"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const DropdownNotification = () => {
  const {
    dropdownNotifications,
    loadMoreNotifications,
    handleNotificationClick,
    anchorElement,
    handleCloseNotification,
    displayedNotifications,
    unseenNotification,
    markAllNotificationsRead,
  } = useNotification();

  const open = Boolean(anchorElement);
  const hasUnread = unseenNotification > 0;
  const hasDropdownItems = dropdownNotifications?.length > 0;

  const timeAgo = (date) => {
    const now = new Date();
    const diffInSeconds = Math.floor((now - new Date(date)) / 1000);
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    const diffInHours = Math.floor(diffInMinutes / 60);
    const diffInDays = Math.floor(diffInHours / 24);

    if (diffInDays > 0) return `${diffInDays} days ago`;
    if (diffInHours > 0) return `${diffInHours} hours ago`;
    if (diffInMinutes > 0) return `${diffInMinutes} minutes ago`;
    return `${diffInSeconds} seconds ago`;
  };

  if ("Notification" in window) {
    if (Notification.permission !== "granted") {
      Notification.requestPermission().then((permission) => {
        if (permission === "granted") {
          // Notification permission granted
        }
      });
    }
  } else {
    console.warn("This browser does not support notifications.");
  }

  return (
    <div className="notification-box mp-notification align-content-center">
      <CustomTooltip title="Notifications" placement="bottom" arrow>
        <button
          type="button"
          className="mp-notification-trigger"
          id="kt_drawer_chat_toggle"
          onClick={handleNotificationClick}
          aria-controls={open ? "notification-menu" : undefined}
          aria-haspopup="true"
          aria-expanded={open ? "true" : undefined}
          aria-label={
            hasUnread
              ? `${unseenNotification} unread notifications`
              : "Notifications"
          }
        >
          <MyNotificationsNoneRoundedIcon className="mp-notification-icon" />
          {hasUnread ? (
            <span className="mp-notification-dot" aria-hidden>
              <span className="mp-notification-dot-core" />
              <span className="mp-notification-dot-pulse" />
            </span>
          ) : null}
        </button>
      </CustomTooltip>

      <Menu
        anchorEl={anchorElement}
        id="notification-menu"
        open={open}
        onClose={handleCloseNotification}
        className="mp-notification-menu"
        disableEnforceFocus
        disableScrollLock
        PaperProps={{
          elevation: 0,
          className: "mp-notification-paper",
        }}
        TransitionComponent={Fade}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <div className="mp-notification-header">
          <div>
            <h5>Notifications</h5>
            <p>Recent account activity</p>
          </div>
          <div className="mp-notification-header-actions">
            {hasUnread ? (
              <button
                type="button"
                className="mp-notification-mark-all"
                onClick={markAllNotificationsRead}
              >
                <CheckCheck size={14} aria-hidden />
                Mark all read
              </button>
            ) : null}
          </div>
        </div>

        {hasDropdownItems ? (
          <div className="mp-notification-list">
            {displayedNotifications?.map((notif) => {
              const meta = getNotificationMeta(notif.message);
              const NotificationIcon = meta.icon;

              return (
                <MenuItem
                  key={notif.id}
                  data-id={notif.id}
                  className={`mp-notification-item${
                    notif.isRead ? "" : " is-unread"
                  }`}
                  disableRipple
                >
                  <span
                    className={`mp-notification-item-icon is-${meta.tone}`}
                    aria-hidden
                  >
                    <NotificationIcon size={17} />
                  </span>
                  <span className="mp-notification-item-body">
                    <span className="mp-notification-text">
                      {notif.message}
                    </span>
                    <span className="mp-notification-time">
                      <Clock3 size={11} aria-hidden />
                      {timeAgo(notif.createdAt)}
                    </span>
                  </span>
                  <span
                    className="mp-notification-unread-marker"
                    aria-label="Unread"
                  />
                </MenuItem>
              );
            })}
          </div>
        ) : (
          <div className="mp-notification-empty">
            <EmptyNotificationsArt />
            <p>No notifications</p>
            <span>You’re all caught up for now.</span>
          </div>
        )}

        <div className="mp-notification-footer">
          {hasDropdownItems ? (
            <>
              <span>
                Showing {displayedNotifications?.length || 0} of{" "}
                {dropdownNotifications.length}
              </span>
              <div className="mp-notification-footer-actions">
                {dropdownNotifications.length !==
                displayedNotifications?.length ? (
                  <button type="button" onClick={loadMoreNotifications}>
                    Load more
                  </button>
                ) : null}
                <Link
                  to="/notifications"
                  className="mp-notification-view-all"
                  onClick={handleCloseNotification}
                >
                  View all
                </Link>
              </div>
            </>
          ) : (
            <>
              <span>Notification history</span>
              <Link
                to="/notifications"
                className="mp-notification-view-all"
                onClick={handleCloseNotification}
              >
                View all notifications
              </Link>
            </>
          )}
        </div>
      </Menu>
    </div>
  );
};

export default DropdownNotification;
